// FLAGS: silent-failure
// Reports interaction, content and auth events to OpenUp.Api's own events endpoint — the one telemetry
// sink in this directory that is not a third party.
import {
  InteractionEventType,
  AuthEventType,
  AIGuideFeedbackEventType,
} from "@src/queries/constants/eventConstants";
import { EventRequest } from "@src/queries/events";
import { apiPaths, ContentEventType, EntityType } from "@src/utils/constants";
import { getApiBaseUrl } from "@src/utils/urlHelpers";
import { buildApiRequestHeaders } from "@src/utils/requestHeaders";
import { OpenUpHttpClient } from "@src/api/OpenUpHttpClient";
import { RequestEventEventRequest } from "@src/api/v1/data-contracts";
import { Events } from "@src/api/v1/Events";

export type LoginMethod = "password" | "external" | "guest" | "refresh";

export interface DataRequest {
  key: string;
  value: string;
}

export interface EventTrackingOptions {
  entityType?: EntityType | null;
  isRecommended?: boolean;
  section?: string;
  additionalData?: DataRequest[];
}

export type TrackableEventType =
  | ContentEventType
  | InteractionEventType
  | AIGuideFeedbackEventType;

// `view` is supplied by the caller: services stay router-free, so the React
// layer (hooks/useEventTracking) owns reading the current pathname.
export const buildEventRequest = (
  entityId: string | number,
  eventType: TrackableEventType,
  options: EventTrackingOptions,
  view: string,
): EventRequest => ({
  eventType: eventType.toString(),
  entityId: entityId.toString(),
  entityType: options.entityType?.toString() || "",
  isRecommended: options.isRecommended || false,
  platform: "web",
  metadata: {
    userAgent: navigator.userAgent,
    view,
    section: options.section,
    data: options.additionalData || [],
  },
});

// Delivery that survives the document going away, for events fired on unload
// where a react-query mutation would be cancelled with the page. Bypassing axios
// means bypassing its request interceptor too, so the shared header builder
// stands in — without it the exit event is the one request with no session
// correlator or trace id, exactly when it is hardest to chase.
export const sendEventKeepalive = (request: EventRequest) => {
  fetch(`${getApiBaseUrl()}${apiPaths.events}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildApiRequestHeaders(),
    },
    body: JSON.stringify(request),
    credentials: "include",
    keepalive: true,
  }).catch((error) => {
    console.error("Failed to send event on exit:", error);
  });
};

// FLAGS: silent-failure
// Same shape: the generated client's `createEvent` is not awaited, so the `catch` sees only synchronous
// construction errors and a lost login or refresh event leaves no trace here.
const sendAuthEvent = (eventType: AuthEventType, loginMethod: LoginMethod) => {
  try {
    const request: RequestEventEventRequest = {
      eventType,
      entityId: "-1",
      entityType: AuthEventType.Login,
      isRecommended: false,
      platform: "web",
      metadata: {
        userAgent: navigator.userAgent,
        data: [{ key: "loginMethod", value: loginMethod }],
      },
    };

    new Events(OpenUpHttpClient).createEvent(request);
    return true;
  } catch (error) {
    return false;
  }
};

export const sendLoginEvent = (loginMethod: LoginMethod) =>
  sendAuthEvent(AuthEventType.Login, loginMethod);

export const sendRefreshTokenEvent = () =>
  sendAuthEvent(AuthEventType.RefreshTokenValidation, "refresh");
